import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import '../styles/form101.css';

const Form101 = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingPrevious, setLoadingPrevious] = useState(true);
  const [currentSection, setCurrentSection] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  const taxYear = new Date().getFullYear();
  const todayDate = new Date().toISOString().slice(0, 10);

  const [formData, setFormData] = useState({
    full_name: user?.name || '',
    id_number: '',
    date_of_birth: '',
    gender: '',
    marital_status: '',
    address_street: '',
    address_city: '',
    address_zip: '',
    phone: user?.phone || '',
    email: user?.email || '',
    israeli_resident: true,
    residency_start_date: '',
    spouse_name: '',
    spouse_id: '',
    spouse_dob: '',
    spouse_has_income: false,
    children: [],
    income_type: 'salary',
    employment_start_date: '',
    is_first_job: false,
    has_other_income: false,
    other_employer_name: '',
    new_immigrant: false,
    discharged_soldier: false,
    academic_degree: false,
    single_parent: false,
    disability: false,
    bank_name: '',
    bank_branch: '',
    account_number: '',
    health_fund_name: '',
  });

  useEffect(() => {
    const fetchLatest = async () => {
      try {
        const response = await api.get('/form101/latest');
        if (response.data.data?.form_data) {
          setFormData(prev => ({ ...prev, ...response.data.data.form_data }));
        }
      } catch (error) {
        // No previous submission
      } finally {
        setLoadingPrevious(false);
      }
    };
    fetchLatest();
  }, []);

  const sections = [
    { key: 'employee', title: 'פרטי העובד', subtitle: 'Employee Details' },
    { key: 'spouse', title: 'פרטי בן/בת הזוג', subtitle: 'Spouse Details' },
    { key: 'children', title: 'ילדים', subtitle: 'Children' },
    { key: 'employment', title: 'הכנסה ותעסוקה', subtitle: 'Employment & Income' },
    { key: 'tax_credits', title: 'נקודות זיכוי', subtitle: 'Tax Credits' },
    { key: 'bank', title: 'פרטי בנק', subtitle: 'Bank Details' },
    { key: 'health_fund', title: 'קופת חולים', subtitle: 'Health Fund' },
  ];

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addChild = () => {
    setFormData(prev => ({
      ...prev,
      children: [...prev.children, { child_name: '', child_id: '', child_dob: '', child_in_custody: true }],
    }));
  };

  const removeChild = (index) => {
    setFormData(prev => ({
      ...prev,
      children: prev.children.filter((_, i) => i !== index),
    }));
  };

  const updateChild = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      children: prev.children.map((child, i) => i === index ? { ...child, [field]: value } : child),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.full_name || !formData.id_number || !formData.date_of_birth || !formData.gender || !formData.address_city) {
      toast.error('נא למלא את כל שדות החובה');
      setCurrentSection(0);
      return;
    }
    if (!/^\d{9}$/.test(formData.id_number)) {
      toast.error('מספר זהות חייב להכיל 9 ספרות');
      setCurrentSection(0);
      return;
    }

    setLoading(true);
    try {
      await api.post('/form101', { tax_year: taxYear, form_data: formData });
      toast.success('טופס 101 הוגש בהצלחה!');
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בשליחת הטופס');
    } finally {
      setLoading(false);
    }
  };

  const renderEmployeeSection = () => (
    <div className="form101-fields">
      <div className="form101-row">
        <div className="form-group">
          <label>שם מלא *</label>
          <input type="text" value={formData.full_name} onChange={e => updateField('full_name', e.target.value)} placeholder="שם מלא" />
        </div>
        <div className="form-group">
          <label>מספר זהות *</label>
          <input type="text" maxLength={9} value={formData.id_number} onChange={e => updateField('id_number', e.target.value.replace(/\D/g, ''))} placeholder="9 ספרות" />
        </div>
      </div>
      <div className="form101-row">
        <div className="form-group">
          <label>תאריך לידה *</label>
          <input type="date" max={todayDate} value={formData.date_of_birth} onChange={e => updateField('date_of_birth', e.target.value)} />
        </div>
        <div className="form-group">
          <label>מין *</label>
          <select value={formData.gender} onChange={e => updateField('gender', e.target.value)}>
            <option value="">-- בחר --</option>
            <option value="male">זכר</option>
            <option value="female">נקבה</option>
          </select>
        </div>
      </div>
      <div className="form101-row">
        <div className="form-group">
          <label>מצב משפחתי</label>
          <select value={formData.marital_status} onChange={e => updateField('marital_status', e.target.value)}>
            <option value="">-- בחר --</option>
            <option value="single">רווק/ה</option>
            <option value="married">נשוי/אה</option>
            <option value="divorced">גרוש/ה</option>
            <option value="widowed">אלמן/ה</option>
          </select>
        </div>
        <div className="form-group">
          <label>טלפון</label>
          <input type="tel" value={formData.phone} onChange={e => updateField('phone', e.target.value)} placeholder="050-0000000" />
        </div>
      </div>
      <div className="form-group">
        <label>רחוב ומספר</label>
        <input type="text" value={formData.address_street} onChange={e => updateField('address_street', e.target.value)} placeholder="רחוב ומספר בית" />
      </div>
      <div className="form101-row">
        <div className="form-group">
          <label>עיר *</label>
          <input type="text" value={formData.address_city} onChange={e => updateField('address_city', e.target.value)} placeholder="עיר מגורים" />
        </div>
        <div className="form-group">
          <label>מיקוד</label>
          <input type="text" value={formData.address_zip} onChange={e => updateField('address_zip', e.target.value)} placeholder="מיקוד" />
        </div>
      </div>
      <div className="form-group">
        <label>דוא״ל</label>
        <input type="email" value={formData.email} onChange={e => updateField('email', e.target.value)} placeholder="email@example.com" />
      </div>
      <label className="form101-checkbox">
        <input type="checkbox" checked={formData.israeli_resident} onChange={e => updateField('israeli_resident', e.target.checked)} />
        <span>תושב/ת ישראל</span>
      </label>
      {formData.israeli_resident && (
        <div className="form-group" style={{ marginTop: '0.75rem' }}>
          <label>תאריך תחילת תושבות בישראל</label>
          <input type="date" max={todayDate} value={formData.residency_start_date} onChange={e => updateField('residency_start_date', e.target.value)} />
        </div>
      )}
    </div>
  );

  const renderSpouseSection = () => (
    <div className="form101-fields">
      {formData.marital_status !== 'married' ? (
        <div className="form101-empty-state">
          <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <p>חלק זה רלוונטי רק למצב משפחתי "נשוי/אה"</p>
          <small>This section is only relevant if marital status is "Married"</small>
        </div>
      ) : (
        <>
          <div className="form101-row">
            <div className="form-group">
              <label>שם בן/בת הזוג</label>
              <input type="text" value={formData.spouse_name} onChange={e => updateField('spouse_name', e.target.value)} placeholder="שם מלא" />
            </div>
            <div className="form-group">
              <label>ת.ז. בן/בת הזוג</label>
              <input type="text" maxLength={9} value={formData.spouse_id} onChange={e => updateField('spouse_id', e.target.value.replace(/\D/g, ''))} placeholder="9 ספרות" />
            </div>
          </div>
          <div className="form-group">
            <label>תאריך לידה</label>
            <input type="date" max={todayDate} value={formData.spouse_dob} onChange={e => updateField('spouse_dob', e.target.value)} />
          </div>
          <label className="form101-checkbox">
            <input type="checkbox" checked={formData.spouse_has_income} onChange={e => updateField('spouse_has_income', e.target.checked)} />
            <span>יש הכנסה לבן/בת הזוג</span>
          </label>
        </>
      )}
    </div>
  );

  const renderChildrenSection = () => (
    <div className="form101-fields">
      {formData.children.length === 0 ? (
        <div className="form101-empty-state">
          <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
            <line x1="23" y1="11" x2="17" y2="11" /><line x1="20" y1="8" x2="20" y2="14" />
          </svg>
          <p>לא נוספו ילדים עדיין</p>
          <small>No children added yet</small>
        </div>
      ) : (
        formData.children.map((child, index) => (
          <div key={index} className="form101-child-card">
            <div className="form101-child-header">
              <span>ילד/ה {index + 1}</span>
              <button type="button" className="btn-remove-child" onClick={() => removeChild(index)}>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="form101-row">
              <div className="form-group">
                <label>שם</label>
                <input type="text" value={child.child_name} onChange={e => updateChild(index, 'child_name', e.target.value)} placeholder="שם הילד/ה" />
              </div>
              <div className="form-group">
                <label>ת.ז.</label>
                <input type="text" maxLength={9} value={child.child_id} onChange={e => updateChild(index, 'child_id', e.target.value.replace(/\D/g, ''))} placeholder="9 ספרות" />
              </div>
            </div>
            <div className="form101-row">
              <div className="form-group">
                <label>תאריך לידה</label>
                <input type="date" max={todayDate} value={child.child_dob} onChange={e => updateChild(index, 'child_dob', e.target.value)} />
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                <label className="form101-checkbox" style={{ marginBottom: 0, flex: 1 }}>
                  <input type="checkbox" checked={child.child_in_custody} onChange={e => updateChild(index, 'child_in_custody', e.target.checked)} />
                  <span>במשמורתי</span>
                </label>
              </div>
            </div>
          </div>
        ))
      )}
      <button type="button" className="btn-add-child" onClick={addChild}>
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        הוסף ילד/ה
      </button>
    </div>
  );

  const renderEmploymentSection = () => (
    <div className="form101-fields">
      <div className="form101-row">
        <div className="form-group">
          <label>סוג הכנסה</label>
          <select value={formData.income_type} onChange={e => updateField('income_type', e.target.value)}>
            <option value="salary">משכורת חודשית</option>
            <option value="hourly">שכר שעתי</option>
            <option value="stipend">מלגה / סטיפנדיה</option>
          </select>
        </div>
        <div className="form-group">
          <label>תאריך תחילת עבודה</label>
          <input type="date" max={todayDate} value={formData.employment_start_date} onChange={e => updateField('employment_start_date', e.target.value)} />
        </div>
      </div>
      <label className="form101-checkbox">
        <input type="checkbox" checked={formData.is_first_job} onChange={e => updateField('is_first_job', e.target.checked)} />
        <span>זהו מקום העבודה הראשון שלי</span>
      </label>
      <label className="form101-checkbox">
        <input type="checkbox" checked={formData.has_other_income} onChange={e => updateField('has_other_income', e.target.checked)} />
        <span>יש לי הכנסה ממקור נוסף</span>
      </label>
      {formData.has_other_income && (
        <div className="form-group" style={{ marginTop: '0.75rem' }}>
          <label>שם המעסיק הנוסף</label>
          <input type="text" value={formData.other_employer_name} onChange={e => updateField('other_employer_name', e.target.value)} placeholder="שם מעסיק / מקור הכנסה" />
        </div>
      )}
    </div>
  );

  const renderTaxCreditsSection = () => (
    <div className="form101-fields">
      <p className="form101-credits-info">סמן/י את נקודות הזיכוי הרלוונטיות עבורך:</p>
      <label className="form101-checkbox">
        <input type="checkbox" checked={formData.new_immigrant} onChange={e => updateField('new_immigrant', e.target.checked)} />
        <span>עולה חדש/ה</span>
      </label>
      <label className="form101-checkbox">
        <input type="checkbox" checked={formData.discharged_soldier} onChange={e => updateField('discharged_soldier', e.target.checked)} />
        <span>חייל/ת משוחרר/ת (תוך 3 שנים מהשחרור)</span>
      </label>
      <label className="form101-checkbox">
        <input type="checkbox" checked={formData.academic_degree} onChange={e => updateField('academic_degree', e.target.checked)} />
        <span>בעל/ת תואר אקדמי</span>
      </label>
      <label className="form101-checkbox">
        <input type="checkbox" checked={formData.single_parent} onChange={e => updateField('single_parent', e.target.checked)} />
        <span>הורה יחיד/ה</span>
      </label>
      <label className="form101-checkbox">
        <input type="checkbox" checked={formData.disability} onChange={e => updateField('disability', e.target.checked)} />
        <span>נכות (100% או עיוור/ת)</span>
      </label>
    </div>
  );

  const renderBankSection = () => (
    <div className="form101-fields">
      <div className="form-group">
        <label>שם הבנק</label>
        <input type="text" value={formData.bank_name} onChange={e => updateField('bank_name', e.target.value)} placeholder="לדוגמה: לאומי, הפועלים, דיסקונט" />
      </div>
      <div className="form101-row">
        <div className="form-group">
          <label>מספר סניף</label>
          <input type="text" value={formData.bank_branch} onChange={e => updateField('bank_branch', e.target.value)} placeholder="מספר סניף" />
        </div>
        <div className="form-group">
          <label>מספר חשבון</label>
          <input type="text" value={formData.account_number} onChange={e => updateField('account_number', e.target.value)} placeholder="מספר חשבון" />
        </div>
      </div>
    </div>
  );

  const renderHealthFundSection = () => (
    <div className="form101-fields">
      <div className="form-group">
        <label>קופת חולים</label>
        <select value={formData.health_fund_name} onChange={e => updateField('health_fund_name', e.target.value)}>
          <option value="">-- בחר קופת חולים --</option>
          <option value="clalit">כללית</option>
          <option value="maccabi">מכבי</option>
          <option value="meuhedet">מאוחדת</option>
          <option value="leumit">לאומית</option>
        </select>
      </div>
    </div>
  );

  const renderSection = () => {
    switch (currentSection) {
      case 0: return renderEmployeeSection();
      case 1: return renderSpouseSection();
      case 2: return renderChildrenSection();
      case 3: return renderEmploymentSection();
      case 4: return renderTaxCreditsSection();
      case 5: return renderBankSection();
      case 6: return renderHealthFundSection();
      default: return null;
    }
  };

  if (loadingPrevious) {
    return (
      <div className="form101-page">
        <div className="loading">טוען נתונים...</div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="form101-page" dir="rtl">
        <div className="form101-success">
          <svg viewBox="0 0 24 24" width="64" height="64" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          <h2>הטופס הוגש בהצלחה!</h2>
          <p>טופס 101 לשנת {taxYear} נשלח לאישור המנהל</p>
          <button className="btn-primary" onClick={() => { setSubmitted(false); setCurrentSection(0); }}>
            מלא טופס חדש
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="form101-page" dir="rtl">
      <div className="form101-header">
        <h1>טופס 101</h1>
        <p className="form101-subtitle">כרטיס עובד - שנת מס {taxYear}</p>
      </div>

      <div className="form101-progress">
        {sections.map((section, index) => (
          <button
            key={section.key}
            className={`form101-step ${index === currentSection ? 'active' : ''} ${index < currentSection ? 'completed' : ''}`}
            onClick={() => setCurrentSection(index)}
          >
            <span className="step-number">{index < currentSection ? '✓' : index + 1}</span>
            <span className="step-title">{section.title}</span>
          </button>
        ))}
      </div>

      <div className="form101-card">
        <div className="form101-section-header">
          <h2>{sections[currentSection].title}</h2>
          <p>{sections[currentSection].subtitle}</p>
        </div>

        {renderSection()}

        <div className="form101-nav">
          {currentSection > 0 && (
            <button className="btn-secondary" onClick={() => setCurrentSection(prev => prev - 1)}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
              הקודם
            </button>
          )}
          <div style={{ flex: 1 }} />
          {currentSection < sections.length - 1 ? (
            <button className="btn-primary" onClick={() => setCurrentSection(prev => prev + 1)}>
              הבא
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          ) : (
            <button className="btn-primary" onClick={handleSubmit} disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }}></span>
                  שולח...
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                  הגש טופס
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Form101;
